/******/ (() => { // webpackBootstrap
/* eslint-disable */
// Open/closed state of each DTA design grid (AC-784), keyed by
// `${sectionId}:${optionIndex}`. product-information replaces the whole
// [data-product-options] block on every variant change, so every
// <product-swatch> is a new element and the state has to outlive it.
const designRowsState = new Map();

class ProductSwatch extends HTMLElement {
  constructor() {
    super();

    /* ===== Bind the event handlers in order to maintain the correct context of 'this' ===== */
    this.onSwatchChangeBound = this.onSwatchChange.bind(this);
    this.onColorSwatchMouseEnterBound = this.onColorSwatchMouseEnter.bind(this);
    this.onSwatchMouseLeaveBound = this.onSwatchMouseLeave.bind(this);
    this.onVariantChangeBound = this.onVariantChange.bind(this);
  }

  connectedCallback() {
    this.colorLabelContainer = this.querySelector('[data-color-swatch-name-container]');
    this.colorLabel = this.querySelector('[data-color-swatch-name]');
    this.colorLabelState = this.querySelector('[data-color-swatch-state]');
    this.soldOutString = this.getAttribute('data-swatch-sold-out-string');
    this.colorSwatches = this.querySelectorAll('.swatch-element.color');
    this.sectionId = this.getAttribute('data-section-id');
    this.setEventListeners();
    this.setColorLabelState();
    if (this.classList.contains('is-dta-design-tiles')) this.initDesignRows();
  }

  initDesignRows() {
    /* ===== Collapse the DTA design grid to its first rows behind a toggle ===== */
    this.rowsToggle = this.querySelector('[data-design-rows-toggle]');
    this.rowsToggleLabel = this.querySelector('[data-design-rows-label]');
    this.rowsWrapper = this.querySelector('.swatch-items-wrapper');
    if (!this.rowsToggle || !this.rowsWrapper) return;

    this.maxRows = parseInt(this.rowsToggle.dataset.rows, 10) || 3;
    this.rowsStateKey = `${this.sectionId}:${this.dataset.optionIndex}`;
    this.onRowsToggleClickBound = this.onRowsToggleClick.bind(this);
    this.rowsToggle.addEventListener('click', this.onRowsToggleClickBound);

    // Lay out now so a re-rendered options block never paints the full grid,
    // then once more in the next frame: product-information narrows the tiles
    // (filterDependentOptionsBySelected) only after this element is connected.
    this.layoutDesignRows();
    this.scheduleDesignRowsLayout();

    // Only a width change can move tiles between rows. The relayout waits for
    // the next frame because it changes the observed element's height, which
    // inside the callback would trip the "ResizeObserver loop" error.
    this.rowsObserver = new ResizeObserver(entries => {
      const width = entries[0].contentRect.width;
      if (width === this.rowsObservedWidth) return;
      this.rowsObservedWidth = width;
      this.scheduleDesignRowsLayout();
    });
    this.rowsObserver.observe(this.rowsWrapper);
  }

  scheduleDesignRowsLayout() {
    cancelAnimationFrame(this.rowsFrame);
    this.rowsFrame = requestAnimationFrame(() => this.layoutDesignRows());
  }

  layoutDesignRows() {
    /* ===== Hide the tiles past maxRows unless the grid is open ===== */
    if (!this.isConnected) return;

    const items = Array.from(this.rowsWrapper.querySelectorAll('.swatch-element'));
    items.forEach(item => item.classList.remove('is-row-hidden'));

    // Rows come from where the tiles actually sit. A tile that starts below the
    // current row's lowest edge opens a new row; comparing tops alone would
    // split a row, because the shorter "Not seen here" tile is centered in it.
    const overflow = [];
    let rows = 0;
    let rowBottom = -Infinity;
    let checkedOverflows = false;
    items.forEach(item => {
      if (!item.getClientRects().length) return; // filtered out by product-information
      const rect = item.getBoundingClientRect();
      if (rect.top >= rowBottom - 1) {
        rows += 1;
        rowBottom = rect.bottom;
      } else {
        rowBottom = Math.max(rowBottom, rect.bottom);
      }
      if (rows <= this.maxRows) return;
      overflow.push(item);
      if (item.querySelector('input[type="radio"]:checked')) checkedOverflows = true;
    });

    // A selection past the cut (e.g. a ?variant= deep link) opens the grid the
    // first time, so the chosen design is visible. After that the shopper's
    // own choice wins.
    if (!designRowsState.has(this.rowsStateKey) && checkedOverflows) {
      designRowsState.set(this.rowsStateKey, true);
    }

    const hasMore = overflow.length > 0;
    const expanded = hasMore && designRowsState.get(this.rowsStateKey) === true;
    if (!expanded) overflow.forEach(item => item.classList.add('is-row-hidden'));

    this.rowsToggle.hidden = !hasMore;
    this.rowsToggle.setAttribute('aria-expanded', String(expanded));
    // Only the label span: the button also holds the chevron icon.
    (this.rowsToggleLabel || this.rowsToggle).textContent = expanded
      ? this.rowsToggle.dataset.labelLess
      : this.rowsToggle.dataset.labelMore;
  }

  onRowsToggleClick() {
    const expanded = this.rowsToggle.getAttribute('aria-expanded') !== 'true';
    designRowsState.set(this.rowsStateKey, expanded);
    this.layoutDesignRows();

    // Collapsing a long grid can leave the button above the viewport.
    if (!expanded && this.rowsToggle.getBoundingClientRect().top < 0) {
      this.rowsToggle.scrollIntoView({ block: 'center' });
    }
  }

  setEventListeners() {
    /* ===== Attach the event listeners to the DOM elements ===== */
    this.colorSwatches.forEach(swatch => {
      swatch.addEventListener('mouseenter', this.onColorSwatchMouseEnterBound);
      swatch.addEventListener('mouseleave', this.onSwatchMouseLeaveBound);
    });

    this.addEventListener('change', this.onSwatchChangeBound);
    eventBus.on('variant:change', this.onVariantChangeBound);
  }

  setColorLabelState() {
    /* ===== Set the color label state based on the active swatch ===== */
    const activeSwatch = this.querySelector('.swatch-element.color.active');
    if (!activeSwatch) return;

    const activeSwatchAvailable = activeSwatch.getAttribute('data-swatch-option-available');
    if (!this.soldOutString || !this.colorLabelState) return;

    // Set color label state (sold out or not)
    this.updateColorLabelState(activeSwatchAvailable);

    /* ===== Keep track of the current label ===== */
    this.currentLabel = '';
    if (this.colorLabel) this.currentLabel = this.colorLabel.textContent;
  }

  updateColorLabelState(available) {
    /* ===== Set the color label state (sold out or not) ===== */
    if (this.colorLabelState) {
      this.colorLabelState.innerText = available === 'false' ? `(${this.soldOutString})` : '';
    }
  }

  onSwatchChange(event) {
    const input = event.target;
    const optionIndex = Number(input.closest('product-swatch').dataset.optionIndex);
    const productOptionsBlock = input.closest('[data-product-options]');
    const productSwatchesCount = productOptionsBlock.querySelectorAll('product-swatch').length;
    const isFirst = optionIndex === 0;
    const isLast = optionIndex + 1 === productSwatchesCount;
    const isSingle = productSwatchesCount === 1;
    const isMiddle = !isFirst && !isLast && !isSingle;
    const isCombinedListing = input.getAttribute('data-is-combined-listing') === 'true';

    if (isFirst && !isSingle) {
      this.clearOtherSwatchSelections(productOptionsBlock, input.closest('product-swatch'));
      this.emitVariantChangeEvent(null, '', '', false, true, false, false, null, false, optionIndex);
    } else if (isMiddle) {
      // When a later option is clicked, the auto-selected earlier options (e.g. default
      // Width) may not combine with the new value — switch them to the first compatible
      // value so the rendered page reflects the user's actual intent.
      if (!isCombinedListing) {
        this.ensureCompatibleEarlierOptions(productOptionsBlock, optionIndex, input.value);
      }

      // === Fix #463 — Big Game (and other past-250-cap designs) main image swap ===
      // Liquid's `product.variants` caps at 250, so designs whose variants live past
      // the cap render with an empty `data-product-fetch-url` and an empty
      // `data-media-id`. The default `_clearLastAfterFetch` + `_autoSelectNextOption`
      // flow ends up emitting `?option_values=W,Design` (no last option), which
      // Shopify cannot resolve for past-cap designs and falls back to the default
      // variant — leaving the main image stuck on the previously selected design.
      //
      // When the inline `data-product-fetch-url` is blank AND the full variants
      // matrix has been hydrated by ensureFullVariantsMatrix() (data-complete=true),
      // resolve a concrete variant ID from the full matrix and emit a direct
      // `?variant=<id>` fetch so Shopify renders the chosen design as
      // `selected_variant` and product-media swaps to its featured_media.
      const hasInlineFetchUrl = !!input.getAttribute('data-product-fetch-url');
      if (!hasInlineFetchUrl && !isCombinedListing) {
        // The matrix may still be hydrating (the proxy fetch in
        // ensureFullVariantsMatrix() is async). Try once synchronously; if it's
        // not ready, kick off a short wait (≤1.5s) then re-resolve and emit.
        // Either path short-circuits the legacy null-emit flow so we never fall
        // back to ?option_values=... for past-cap designs.
        if (this.isFullMatrixComplete(productOptionsBlock)) {
          if (this._tryEmitResolvedVariant(productOptionsBlock, optionIndex)) return;
        } else {
          this.waitForFullMatrix(productOptionsBlock).then(complete => {
            if (complete) this._tryEmitResolvedVariant(productOptionsBlock, optionIndex);
          });
          // Don't fall through to the legacy null-emit; the async path will emit
          // the correct variant once the matrix arrives. (If the proxy never
          // responds the user can just click another swatch.)
          return;
        }
      }
      // === /Fix #463 ===

      // Remember the currently-selected last option (e.g. Size) so we can restore it
      // after the page re-renders, instead of falling back to the first compatible value.
      const previousLastValue = this.getLastSwatchSelectedValue(productOptionsBlock);

      // === Fix #564 — collapse the middle-option (Design) two-phase re-render ===
      // The legacy path below clears the last option and fetches the null-variant
      // page (one DOM swap + focus restore), then auto-selects the dependent
      // option and fetches AGAIN (a second swap + restore). VoiceOver therefore
      // announces the design three times (e.g. "Dapper Dog / Original Designs /
      // Dapper Dog") and the second swap cuts the first announcement off mid-word.
      // When the full variants matrix is trustworthy we resolve the whole variant
      // in a single shot — preferring the user's previous last-option value when it
      // still combines, otherwise the first compatible one — and emit ONE valid
      // variant:change. That yields a single re-render + single focus restore, so
      // the design is announced at most twice (name + state) with no mid-render
      // focus race. Falls back to the legacy clear + auto-select flow when the
      // matrix is incomplete (e.g. >250 variants still hydrating) or no compatible
      // variant exists, preserving the prior behaviour.
      if (!isCombinedListing && this.isFullMatrixComplete(productOptionsBlock)) {
        if (this._tryEmitResolvedMiddleVariant(productOptionsBlock, optionIndex, previousLastValue)) return;
      }
      // === /Fix #564 ===

      this.clearLastSwatchSelection(productOptionsBlock);
      this.emitVariantChangeEvent(null, '', '', false, false, true, true, previousLastValue, false, optionIndex);
    } else {
      let variant = null;
      let productUrl = input.getAttribute('data-product-url');
      let productFetchUrl = input.getAttribute('data-product-fetch-url');

      if (!isSingle && !isCombinedListing) {
        this.ensureCompatibleEarlierOptions(productOptionsBlock, optionIndex, input.value);
        // The variant attached to this option value (value.variant) may belong to a
        // different earlier-option combo than what's now checked. Re-resolve from the
        // variant matrix so the fetched page matches the actually selected combination.
        const resolved = this.resolveVariantFromCheckedOptions(productOptionsBlock);
        const productInfo = this.closest('product-information');
        if (resolved && productInfo) {
          variant = { id: resolved.id, available: resolved.available };
          productUrl = productInfo.dataset.url;
          productFetchUrl = `${productUrl}?variant=${resolved.id}`;
        }
      }

      if (!variant) {
        variant = this.getVariantData(input.id);
      }
      // When a "Not seen here" design is part of the selected combination, the
      // resolved variant has no media of its own — tell product-media to fall
      // back to the product's default image instead of a (missing) variant image.
      const resetMediaToDefault = this.isNotSeenHereSelected(productOptionsBlock);
      this.emitVariantChangeEvent(variant, productFetchUrl, productUrl, isCombinedListing, false, false, false, null, resetMediaToDefault, optionIndex);
    }
  }

  clearLastSwatchSelection(productOptionsBlock) {
    const swatches = productOptionsBlock.querySelectorAll('product-swatch');
    const lastSwatch = swatches[swatches.length - 1];
    if (!lastSwatch) return;
    lastSwatch.querySelectorAll('input[type="radio"]').forEach(input => {
      input.checked = false;
    });
    lastSwatch.querySelectorAll('.swatch-element.active').forEach(el => {
      el.classList.remove('active');
    });
  }

  getLastSwatchSelectedValue(productOptionsBlock) {
    const swatches = productOptionsBlock.querySelectorAll('product-swatch');
    const lastSwatch = swatches[swatches.length - 1];
    if (!lastSwatch) return null;
    const checked = lastSwatch.querySelector('input[type="radio"]:checked');
    return checked ? checked.value : null;
  }

  ensureCompatibleEarlierOptions(productOptionsBlock, currentIndex, currentValue) {
    const variants = this.getVariantsMatrix(productOptionsBlock);
    if (!variants) return;

    const swatches = Array.from(productOptionsBlock.querySelectorAll('product-swatch'));
    const constraints = { [`option${currentIndex + 1}`]: currentValue };

    for (let j = 0; j < currentIndex; j++) {
      const earlierSwatch = swatches[j];
      const earlierKey = `option${j + 1}`;
      const earlierChecked = earlierSwatch.querySelector('input[type="radio"]:checked');
      const earlierValue = earlierChecked ? earlierChecked.value : null;

      const stillWorks = earlierValue != null && variants.some(v =>
        this.variantMatchesConstraints(v, { ...constraints, [earlierKey]: earlierValue })
      );

      if (stillWorks) {
        constraints[earlierKey] = earlierValue;
        continue;
      }

      const inputs = Array.from(earlierSwatch.querySelectorAll('input[type="radio"]'));
      let chosen = null;
      for (const inp of inputs) {
        const ok = variants.some(v =>
          this.variantMatchesConstraints(v, { ...constraints, [earlierKey]: inp.value })
        );
        if (ok) {
          chosen = inp;
          constraints[earlierKey] = inp.value;
          break;
        }
      }
      if (!chosen) continue;

      inputs.forEach(inp => { inp.checked = false; });
      chosen.checked = true;
      earlierSwatch.querySelectorAll('.swatch-element').forEach(el => {
        const inp = el.querySelector('input[type="radio"]');
        el.classList.toggle('active', !!(inp && inp.value === chosen.value));
      });
    }
  }

  isNotSeenHereSelected(productOptionsBlock) {
    // True when any currently-checked swatch is the "Not seen here" placeholder
    // design (flagged with data-not-seen-here in swatch.liquid). Such a design
    // has no variant media, so the gallery should show the product default.
    return !!productOptionsBlock.querySelector('input[data-not-seen-here="true"]:checked');
  }

  resolveVariantFromCheckedOptions(productOptionsBlock) {
    const variants = this.getVariantsMatrix(productOptionsBlock);
    if (!variants) return null;
    const swatches = productOptionsBlock.querySelectorAll('product-swatch');
    const constraints = {};
    swatches.forEach((swatch, i) => {
      const checked = swatch.querySelector('input[type="radio"]:checked');
      if (checked) constraints[`option${i + 1}`] = checked.value;
    });
    return variants.find(v => this.variantMatchesConstraints(v, constraints)) || null;
  }

  variantMatchesConstraints(variant, constraints) {
    if (!variant.available) return false;
    for (const key of Object.keys(constraints)) {
      if (variant[key] !== constraints[key]) return false;
    }
    return true;
  }

  getVariantsMatrix(productOptionsBlock) {
    const productInfo = productOptionsBlock.closest('product-information');
    if (!productInfo) return null;
    const matrixEl = productInfo.querySelector('[data-product-variants-matrix]');
    if (!matrixEl) return null;
    try {
      const parsed = JSON.parse(matrixEl.textContent);
      return Array.isArray(parsed) && parsed.length ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  // Whether ensureFullVariantsMatrix() has replaced the inline (Liquid-capped at
  // 250) matrix with the full set pulled through the embroidery app proxy. Used
  // by the Fix-#463 branch in onSwatchChange to decide if it can safely resolve
  // past-cap design variants to a direct ?variant=<id> URL. When false, we fall
  // back to the legacy clear-last + auto-select-next flow rather than risk an
  // incorrect variant id from the truncated inline matrix.
  isFullMatrixComplete(productOptionsBlock) {
    const productInfo = productOptionsBlock.closest('product-information');
    if (!productInfo) return false;
    const matrixEl = productInfo.querySelector('[data-product-variants-matrix]');
    if (!matrixEl) return false;
    return matrixEl.dataset.complete === 'true';
  }

  // Polls the matrix's data-complete flag for up to ~1.5s. Resolves to true if
  // the proxy hydration finished in time, false otherwise. The caller decides
  // what to do on timeout (the Fix-#463 branch currently just gives up — the
  // user can click another swatch).
  waitForFullMatrix(productOptionsBlock, timeoutMs = 1500, stepMs = 50) {
    return new Promise(resolve => {
      const start = Date.now();
      const tick = () => {
        if (this.isFullMatrixComplete(productOptionsBlock)) {
          resolve(true);
          return;
        }
        if (Date.now() - start >= timeoutMs) {
          resolve(false);
          return;
        }
        setTimeout(tick, stepMs);
      };
      tick();
    });
  }

  // Shared body of the Fix-#463 resolve-and-emit path. Returns true when it
  // emitted a valid variant:change so the caller can short-circuit; false when
  // resolution failed and the caller should fall back to legacy behaviour.
  _tryEmitResolvedVariant(productOptionsBlock, originOptionIndex = null) {
    const resolved = this.resolveVariantFromCheckedOptions(productOptionsBlock);
    const productInfo = this.closest('product-information');
    if (!resolved || !productInfo) return false;
    const productUrl = productInfo.dataset.url;
    const productFetchUrl = `${productUrl}?variant=${resolved.id}`;
    const variant = { id: resolved.id, available: resolved.available };
    // Sync all option radios to match the resolved variant before emitting so
    // synchronous variant:change listeners (e.g. Leash Add-On areAllOptionsSelected)
    // see a complete DOM selection at emit time. The section re-render that follows
    // will overwrite these transient radio states anyway.
    this.syncCheckedRadiosToVariant(productOptionsBlock, resolved);
    this.emitVariantChangeEvent(variant, productFetchUrl, productUrl, false, false, false, false, null, false, originOptionIndex);
    return true;
  }

  // Resolve a full variant after a MIDDLE option (e.g. Design) change without the
  // legacy two-phase clear-last + auto-select-next re-render (see Fix #564 in
  // onSwatchChange). Constraints come from every option EXCEPT the last swatch
  // (i.e. the earlier options plus the just-changed middle value), mirroring the
  // legacy "keep everything, only re-pick the last option" semantics. We prefer a
  // variant whose last-option value matches the user's previous selection so the
  // size is preserved; otherwise we take the first compatible available variant.
  // Emits a single valid variant:change so focus restores once. Returns true when
  // it emitted (caller short-circuits); false → caller falls back to the legacy
  // flow (matrix unusable here or no compatible variant — show "unavailable").
  _tryEmitResolvedMiddleVariant(productOptionsBlock, optionIndex, previousLastValue) {
    const variants = this.getVariantsMatrix(productOptionsBlock);
    const productInfo = this.closest('product-information');
    if (!variants || !productInfo) return false;

    const swatches = productOptionsBlock.querySelectorAll('product-swatch');
    if (swatches.length < 2) return false;
    const lastKey = `option${swatches.length}`;

    // ensureCompatibleEarlierOptions() already reconciled the earlier options, so
    // reading their checked state is accurate. Skip the last swatch — its value is
    // what we're resolving below.
    const constraints = {};
    for (let i = 0; i < swatches.length - 1; i++) {
      const checked = swatches[i].querySelector('input[type="radio"]:checked');
      if (checked) constraints[`option${i + 1}`] = checked.value;
    }

    const candidates = variants.filter(v => this.variantMatchesConstraints(v, constraints));
    if (!candidates.length) return false;

    let target = null;
    if (previousLastValue != null) {
      target = candidates.find(v => v[lastKey] === previousLastValue) || null;
    }
    if (!target) target = candidates[0];
    if (!target) return false;

    const productUrl = productInfo.dataset.url;
    const productFetchUrl = `${productUrl}?variant=${target.id}`;
    const variant = { id: target.id, available: target.available };
    // When the chosen design is the "Not seen here" placeholder, its variant has
    // no media of its own — tell product-media to fall back to the default image.
    const resetMediaToDefault = this.isNotSeenHereSelected(productOptionsBlock);
    this.emitVariantChangeEvent(variant, productFetchUrl, productUrl, false, false, false, false, null, resetMediaToDefault, optionIndex);
    return true;
  }
  
  syncCheckedRadiosToVariant(productOptionsBlock, resolvedVariant) {
    const swatches = productOptionsBlock.querySelectorAll('product-swatch');
    swatches.forEach((swatch, i) => {
        const targetValue = resolvedVariant[`option${i + 1}`];
        if (targetValue == null) return;
        let matched = false;
        swatch.querySelectorAll('input[type="radio"]').forEach((input) => {
          if (input.value === targetValue) {
            input.checked = true;
            matched = true;
          } else {
            input.checked = false;
          }
        });
        if (!matched) return;
        swatch.querySelectorAll('.swatch-element').forEach((el) => {
          const inp = el.querySelector('input[type="radio"]');
          el.classList.toggle('active', !!(inp && inp.value === targetValue));
        });
    });
  }

  clearOtherSwatchSelections(productOptionsBlock, currentSwatch) {
    productOptionsBlock.querySelectorAll('product-swatch').forEach(swatch => {
      if (swatch === currentSwatch) return;
      swatch.querySelectorAll('input[type="radio"]').forEach(input => {
        input.checked = false;
      });
      swatch.querySelectorAll('.swatch-element.active').forEach(el => {
        el.classList.remove('active');
      });
    });
  }

  onVariantChange(context) {
    if (context.sectionId && context.sectionId !== this.sectionId) return;
    this.currentVariant = context.variant;
  }

  emitVariantChangeEvent(variant, productFetchUrl, productUrl, isCombinedListing = false, clearAfterFetch = false, autoSelectNext = false, clearLastAfterFetch = false, preferredLastOptionValue = null, resetMediaToDefault = false, originOptionIndex = null) {
    /* ===== Emit the variant:change event ===== */
    eventBus.emit('variant:change', {
      sectionId: this.sectionId,
      variant: variant,
      fetchURL: productFetchUrl,
      productURL: productUrl,
      isCombinedListing: isCombinedListing,
      clearAfterFetch: clearAfterFetch,
      autoSelectNext: autoSelectNext,
      clearLastAfterFetch: clearLastAfterFetch,
      preferredLastOptionValue: preferredLastOptionValue,
      resetMediaToDefault: resetMediaToDefault,
      // Index of the option group the user actually changed, so focus can be
      // restored there after the options re-render (WCAG 3.2.2).
      originOptionIndex: originOptionIndex,
    });
  }

  onColorSwatchMouseEnter(event) {
    /* ===== Update the color label on mouse enter ===== */
    this.updateColorLabel(event.currentTarget);
    
    // Add sibling hover active class
    const activeSwatch = this.querySelector('.swatch-element.color.active');
    if (activeSwatch && !event.currentTarget.classList.contains('active')) activeSwatch.classList.add('sibling-hover-active');
  }

  onSwatchMouseLeave() {
    /* ===== Reset the color label on mouse leave ===== */
    // Remove sibling hover active class
    const siblingHoverActiveSwatch = this.querySelector('.swatch-element.sibling-hover-active');
    if (siblingHoverActiveSwatch) siblingHoverActiveSwatch.classList.remove('sibling-hover-active');

    // Reset color label
    this.resetColorLabel();
  }

  updateColorLabel = (swatch) => {
    /* ===== Update the color label based on the swatch value ===== */
    if (!swatch) return;
    const label = swatch.getAttribute('data-value');
    const swatchAvailable = swatch.getAttribute('data-swatch-option-available');

    if (!label || !this.colorLabel) return;
    // Set color label text
    this.colorLabel.textContent = label;
    this.animateColorLabel(label);
    // Set color label state (sold out or not)
    this.updateColorLabelState(swatchAvailable);
  }

  resetColorLabel = () => {
    /* ===== Reset the color label to the active swatch value ===== */
    const activeSwatch = this.querySelector('.swatch-element.color.active');
    if (!activeSwatch || !this.colorLabel) return;

    const activeSwatchValue = activeSwatch.getAttribute('data-value');
    const swatchAvailable = activeSwatch.getAttribute('data-swatch-option-available');
    if (!activeSwatchValue) return;

    // Reset color label text
    this.colorLabel.textContent = activeSwatchValue;
    this.animateColorLabel(activeSwatchValue);
    // Reset color label state (sold out or not)
    this.updateColorLabelState(swatchAvailable);
  }

  animateColorLabel(label) {
    if (label === this.currentLabel) return;

    /* ===== Animate the color label if label has changed ===== */
    this.colorLabelContainer.classList.remove('fade-in-label');
    void this.colorLabelContainer.offsetWidth; // Force reflow to reset the animation
    this.colorLabelContainer.classList.add('fade-in-label');

    /* ===== Keep track of the current label ===== */
    this.currentLabel = label;
  }

  removeEventListeners() {
    /* ===== Remove the event listeners from the DOM elements ===== */
    this.colorSwatches.forEach(swatch => {
      swatch.removeEventListener('mouseenter', this.onColorSwatchMouseEnterBound);
      swatch.removeEventListener('mouseleave', this.onSwatchMouseLeaveBound);
    });

    this.removeEventListener('change', this.onSwatchChangeBound);
    eventBus.off('variant:change', this.onVariantChangeBound);
  }

  getVariantData(inputId) {
    return JSON.parse(this.getVariantDataElement(inputId).textContent);
  }

  getVariantDataElement(inputId) {
    return this.querySelector(`script[type="application/json"][data-resource="${inputId}"]`);
  }

  disconnectedCallback() {
    /* ===== Remove the event listeners when the element is removed from the DOM ===== */
    this.removeEventListeners();
    if (this.rowsObserver) this.rowsObserver.disconnect();
    cancelAnimationFrame(this.rowsFrame);
  }
}

if (!window.customElements.get('product-swatch')) {
  window.customElements.define('product-swatch', ProductSwatch);
}

/******/ })()
;